import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { User, Mail, Phone, Building, Calendar, FileText, Upload, CheckCircle, X } from 'lucide-react';

interface IFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  desiredDeliveryDate: string;
  attachments: File[];
  agreed: boolean;
}

const QuoteForm = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState<IFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    desiredDeliveryDate: '',
    attachments: [],
    agreed: false,
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'file') {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        setFormData((prevState) => ({
          ...prevState,
          attachments: [...prevState.attachments, ...Array.from(files)],
        }));
      }
    } else if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prevState) => ({
        ...prevState,
        [name]: checked,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleRemoveFile = (index: number) => {
    setFormData((prevState) => ({
      ...prevState,
      attachments: prevState.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Construct Mailto Link with CRLF for better compatibility
    const recipient = "ygm1028@powerteck.co.kr";
    const subject = `[견적문의] ${formData.company} - ${formData.name}`;
    const bodyContent = `
[견적 문의 내용]

1. 회사명: ${formData.company}
2. 담당자: ${formData.name}
3. 이메일: ${formData.email}
4. 연락처: ${formData.phone}
5. 희망 납기일: ${formData.desiredDeliveryDate}

6. 문의 내용:
${formData.message}

--------------------------------------------------
* 본 메일은 홈페이지 견적 문의를 통해 작성되었습니다.
* 첨부파일이 있는 경우, 메일 작성 창에서 직접 파일을 첨부해주세요.
    `.trim();

    // Alert for file attachments
    if (formData.attachments.length > 0) {
      alert("보안 정책상 웹사이트에서 메일 앱으로 파일을 직접 전송할 수 없습니다.\n메일 작성 창이 열리면 선택하신 파일을 직접 첨부해서 보내주세요.");
    }

    try {
      // Try creating a temporary link approach which is often cleaner
      const mailtoLink = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyContent.replace(/\n/g, '\r\n'))}`;

      // Check length - rudimentary check for very long URLs that might fail silently
      if (mailtoLink.length > 2000) {
        alert("내용이 너무 길어 메일 연동에 실패할 수 있습니다. 메일 내용이 잘리면 직접 내용을 붙여넣어주세요.");
      }

      const link = document.createElement('a');
      link.href = mailtoLink;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Fallback message after attempting to open mail client
      setTimeout(() => {
        alert("메일 클라이언트가 열리지 않으면, 아래 주소로 직접 문의주세요: ygm1028@powerteck.co.kr");
      }, 1000); // Small delay to allow mail client to open first

    } catch (err) {
      alert("메일 클라이언트를 여는 중 오류가 발생했습니다. ygm1028@powerteck.co.kr 로 직접 문의 부탁드립니다.");
    }

    setLoading(false);
    // Optional: Reset form or leave it for user reference
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/banner.jpg)` }}
        >
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        <div className="relative z-10 text-center px-4 animate-slide-up">
          <span className="text-secondary font-bold tracking-widest uppercase mb-4 block">Contact Us</span>
          <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6">견적 문의</h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto font-light">
            성공적인 비즈니스를 위한 최적의 파트너, 파워테크가 함께하겠습니다.
          </p>
        </div>
      </section>

      <div className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100">
            <div className="p-8 md:p-12">
              <div className="mb-10 text-center">
                <h2 className="text-3xl font-display font-bold text-slate-900 mb-4">프로젝트 문의하기</h2>
                <p className="text-slate-500">
                  작성하신 내용은 메일 클라이언트로 연동되어 전송됩니다.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Company & Name */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="company" className="text-sm font-bold text-slate-700 ml-1">회사명</label>
                    <div className="relative">
                      <Building className="absolute left-4 top-3.5 text-slate-400" size={20} />
                      <input
                        type="text" id="company" name="company"
                        value={formData.company} onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                        placeholder="(주)파워테크"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-bold text-slate-700 ml-1">담당자 성함 <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <User className="absolute left-4 top-3.5 text-slate-400" size={20} />
                      <input
                        type="text" id="name" name="name" required
                        value={formData.name} onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                        placeholder="홍길동 과장"
                      />
                    </div>
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-bold text-slate-700 ml-1">이메일 <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-3.5 text-slate-400" size={20} />
                      <input
                        type="email" id="email" name="email" required
                        value={formData.email} onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                        placeholder="example@company.com"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-sm font-bold text-slate-700 ml-1">연락처 <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-3.5 text-slate-400" size={20} />
                      <input
                        type="tel" id="phone" name="phone" required
                        value={formData.phone} onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                        placeholder="010-1234-5678"
                      />
                    </div>
                  </div>
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <label htmlFor="desiredDeliveryDate" className="text-sm font-bold text-slate-700 ml-1">희망 납기일</label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-3.5 text-slate-400" size={20} />
                    <input
                      type="date" id="desiredDeliveryDate" name="desiredDeliveryDate"
                      value={formData.desiredDeliveryDate} onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-bold text-slate-700 ml-1">문의 내용 <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <FileText className="absolute left-4 top-4 text-slate-400" size={20} />
                    <textarea
                      id="message" name="message" required
                      value={formData.message} onChange={handleChange}
                      rows={6}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all resize-none"
                      placeholder="프로젝트 개요, 사양, 수량 등 상세 내용을 적어주세요."
                    ></textarea>
                  </div>
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">첨부 파일</label>
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-primary transition-colors bg-slate-50 cursor-pointer relative">
                    <input
                      type="file" id="attachments" name="attachments" multiple
                      onChange={handleChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center pointer-events-none">
                      <Upload className="text-slate-400 mb-2" size={32} />
                      <p className="text-sm text-slate-600 font-medium">클릭하거나 파일을 이곳으로 드래그하세요</p>
                      <p className="text-xs text-slate-400 mt-1">도면, 사양서 등 관련 자료 (최대 10MB)</p>
                    </div>
                  </div>

                  {/* File List */}
                  {formData.attachments.length > 0 && (
                    <ul className="space-y-2 mt-4">
                      {formData.attachments.map((file, index) => (
                        <li key={index} className="flex items-center justify-between bg-blue-50 px-4 py-3 rounded-lg border border-blue-100">
                          <div className="flex items-center space-x-3 overflow-hidden">
                            <FileText size={16} className="text-primary flex-shrink-0" />
                            <span className="text-sm text-slate-700 truncate">{file.name}</span>
                            <span className="text-xs text-slate-400">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            className="text-slate-400 hover:text-red-500 transition-colors p-1"
                          >
                            <X size={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Privacy Policy */}
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                  <h4 className="text-sm font-bold text-slate-900 mb-2">개인정보 수집 및 이용 동의</h4>
                  <p className="text-xs text-slate-500 leading-relaxed mb-4">
                    파워테크(주)는 견적 문의 처리를 위해 최소한의 개인정보를 수집하고 있습니다.
                    수집된 정보는 고객 문의 응대 목적 이외의 용도로는 사용되지 않습니다.
                  </p>
                  <label className="flex items-center cursor-pointer group">
                    <div className="relative flex items-center">
                      <input
                        type="checkbox" name="agreed"
                        checked={formData.agreed} onChange={handleChange}
                        className="peer sr-only"
                      />
                      <div className="w-5 h-5 border-2 border-slate-300 rounded peer-checked:bg-primary peer-checked:border-primary transition-all"></div>
                      <CheckCircle size={14} className="absolute left-0.5 top-0.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity" />
                    </div>
                    <span className="ml-3 text-sm font-medium text-slate-600 group-hover:text-primary transition-colors">
                      개인정보 수집 및 이용에 동의합니다.
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!formData.agreed || loading}
                  className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transition-all transform duration-200 
                                ${formData.agreed && !loading
                      ? 'bg-gradient-to-r from-primary to-blue-700 text-white hover:shadow-xl hover:-translate-y-1'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      전송 중...
                    </span>
                  ) : '견적 문의 보내기'}
                </button>

              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteForm;
